import { useDispatch, useSelector } from 'react-redux';
import { usePubSubEvent } from '../../../hooks/usePubSubEvent';
import {
  configuratorActions,
  selectAddRenderersCount,
  selectBackupDefaultTextures,
  selectDefaultTextures,
  selectInitialMaterialApplied,
  selectInitialObjectProperties,
  selectLabelLinkingInProgress,
  selectMaterialToUpdate,
  selectObjectsWithDefaultThumbnail,
  selectSelectedSceneId,
  selectUnityLoaded,
} from '../../../redux/slicers/configurator/configurator.slicer';
import { getAppState } from '../../../redux/store';
import { CONFIGURATOR_UNITY_EVENT } from '../unity/unityCallbacks';
import { configuratorModule } from '../unity/unityMethods';
import { getSceneJson } from '../unity/initUnity';
import { loadUnityMaterialDetails } from '../../../pages/admin/curator/projectParser/loadUnityMaterialDetails';
import { setUnityObjectImagesProcessing } from '../../../redux/slicers/admin/curatorLoaderSlicer';
import { updateDefaultTextureData } from '../utils';
import { useEffect } from 'react';
import { configuratorUnityObjectPropertiesActions } from '../../../redux/slicers/configurator/configuratorUnityObjectProperties.slicer';
import { uniq } from 'lodash';

export const useInitialObjectProperties = () => {
  const dispatch = useDispatch();
  const defaultData = useSelector(selectDefaultTextures);

  useEffect(() => {
    if (defaultData && !defaultData?.length) {
      processAllData();
      console.warn('all data of objects has been processed');
    }
  }, [defaultData]);

  usePubSubEvent(CONFIGURATOR_UNITY_EVENT.ApplyMaterialPropertiesFinishedReceive, () => {
    applyPropertiesOnObject();
  });

  // Added to ensure SelectMaterialUsingMaterialNameForRetailAutomation is triggered only after receiving SelectedVariationPropFinished from Unity
  usePubSubEvent(CONFIGURATOR_UNITY_EVENT.SelectedVariationPropFinished, () => {});

  usePubSubEvent(CONFIGURATOR_UNITY_EVENT.OnGroupPropLoadedForRetailAutomationCompletely, () => {});

  usePubSubEvent(CONFIGURATOR_UNITY_EVENT.OnReceiveAllInitialObjectProperties, () => {
    const addRendersCount = selectAddRenderersCount(getAppState());
    let count = addRendersCount - 1;
    if (count < 0) {
      count = 0;
    }
    dispatch(configuratorActions.setAddRenderersCount(count));
    if (count === 0) {
      getAllDefaultTextures();
    }
  });

  usePubSubEvent(CONFIGURATOR_UNITY_EVENT.OnReceiveInitialObjectProperties, (_, data) => {
    const allProperties = JSON.parse(JSON.stringify(selectInitialObjectProperties(getAppState())));
    const index = allProperties.findIndex((val) => val.objectName === data.objectName);
    if (index === -1) {
      dispatch(configuratorActions.setInitialObjectProperties(data));
    }
  });

  const getUniqueTexturesFromUnity = (data) => {
    configuratorModule.GetBase64DataUsingTextureName(data);
  };

  const getAllDefaultTextures = () => {
    const storedDefaultBackupData = selectBackupDefaultTextures(getAppState());
    const storedDefaultData = selectDefaultTextures(getAppState()) || [];
    const initialObjectProperties = selectInitialObjectProperties(getAppState());
    const objectsWithDefaultThumbnail = selectObjectsWithDefaultThumbnail(getAppState());
    const defaultTextures = objectsWithDefaultThumbnail.map((val) => val.textureName);
    let defaultData = [];
    const dataToSend = [];
    initialObjectProperties.map((object) => {
      let textureNameList = [
        object?.diffuseName,
        object?.normalMapName,
        object?.aoMapName,
        object?.roughnessMapName,
        object?.metallicMapName,
        object?.transparencyMapName,
      ].filter(Boolean);
      textureNameList = uniq(textureNameList);
      const updatedTextureList = [];
      let allTextures = defaultData.reduce((acc, curr) => {
        acc = acc.concat(curr.textures);
        return acc;
      }, []);
      allTextures = allTextures.concat(defaultTextures);
      textureNameList.map((val) => {
        const isPresent = allTextures.find((ins) => ins === val);
        if (!isPresent) {
          updatedTextureList.push(val);
        }
      });
      if (updatedTextureList.length) {
        const data = {
          objectName: object.objectName,
          textures: updatedTextureList,
        };
        defaultData = defaultData.concat(data);
      }
    });
    if (defaultData.length) {
      defaultData.map((val) => {
        val.textures.map((ins) => {
          const obj = {
            objectName: val.objectName,
            textureName: ins,
          };
          dataToSend.push(obj);
        });
      });
    }
    const finalDataBackup = [];
    const finalDataOriginal = [];
    dataToSend.map((val) => {
      const isPresentBackup = storedDefaultBackupData.find(
        (ins) => ins.textureName === val.textureName
      );
      const isPresentOriginal = storedDefaultData.find(
        (ins) => ins.textureName === val.textureName
      );
      if (!isPresentBackup) {
        finalDataBackup.push(val);
      }
      if (!isPresentOriginal) {
        finalDataOriginal.push(val);
      }
    });
    const combinedDataBackup = [...storedDefaultBackupData, ...finalDataBackup];
    const combinedDataOriginal = [...storedDefaultData, ...finalDataOriginal];
    const updatedData = [];
    combinedDataOriginal.map((val) => {
      const isPresent = objectsWithDefaultThumbnail.find(
        (ins) => ins.textureName === val.textureName
      );
      if (!isPresent) {
        updatedData.push(val);
      }
    });
    if (finalDataBackup.length) {
      getUniqueTexturesFromUnity(finalDataBackup);
    }
    dispatch(configuratorActions.setDefaultTextures(updatedData));
    dispatch(configuratorActions.setBackupDefaultTextures(combinedDataBackup));
  };

  const processAllData = () => {
    dispatch(configuratorActions.setAllDataProcessed(true));
    dispatch(configuratorActions.setDefaultTextures(null));
    dispatch(configuratorActions.setAddRenderersCount(0));
    // A drag-to-label flow (Labels.js) may still be running its own loader lifecycle
    // for the object(s) that triggered this callback — defer to it instead of forcing
    // the loader off underneath it.
    if (!selectLabelLinkingInProgress(getAppState())) {
      setUnityObjectImagesProcessing(false);
    }
    const initialMaterialApplied = selectInitialMaterialApplied(getAppState());
    if (!initialMaterialApplied) {
      applyMaterialsSavedInJSON();
    }
  };

  const applyPropertiesOnObject = () => {
    const materialToUpdate = selectMaterialToUpdate(getAppState());
    const copyData = JSON.parse(JSON.stringify(materialToUpdate));
    if (!copyData?.length) {
      setMaterialProgress(false);
      return;
    }
    const index = copyData.findIndex((val) => !val?.sendToUpdate);
    if (index !== -1) {
      copyData[index].sendToUpdate = true;
      const isDefaultData = copyData[index].isDefaultData;
      dispatch(configuratorActions.setMaterialToUpdate(copyData));
      configuratorModule.SetMaterialPropertiesOnGivenObject({
        objectName: copyData[index].objectName,
        objectData: copyData[index],
        isDefaultData,
      });
    } else {
      dispatch(configuratorActions.setMaterialToUpdate([]));
      setMaterialProgress(false);
    }
  };

  const setMaterialProgress = (flag = false) => {
    dispatch(configuratorUnityObjectPropertiesActions.setApplyMaterialInProgress(flag));
  };

  const resetProperties = () => {
    setUnityObjectImagesProcessing(false);
    dispatch(configuratorActions.setBackupDefaultTextures([]));
  };

  const getMaterialDetails = async () => {
    const sceneId = selectSelectedSceneId(getAppState());
    const sceneJson = await getSceneJson(sceneId, false);
    return sceneJson?.materialDetails || [];
  };

  const applyMaterialsSavedInJSON = async () => {
    const materialDetails = await getMaterialDetails();
    const updatedMaterialDetails = materialDetails?.filter(
      (val) => val?.loadedMaterialId !== 'Not Changed' && val?.materialId !== 'Not Changed'
    );
    if (!updatedMaterialDetails.length) {
      resetProperties();
      dispatch(configuratorActions.setInitialMaterialApplied(true));
      return;
    }
    const objectsWithDefaultThumbnail = selectObjectsWithDefaultThumbnail(getAppState());
    const allStoredTextures = objectsWithDefaultThumbnail.map((val) => val?.textureName);
    updatedMaterialDetails.map((object, index) => {
      let isPresent = true;
      const textures = [
        object?.AOId,
        object?.diffuseId,
        object?.metallicId,
        object?.normalId,
        object?.roughnessId,
        object?.transparencyId,
      ].filter((val) => {
        const isNumber = /^\d+$/.test(val);
        return val && !isNumber;
      });
      textures.map((val) => {
        if (allStoredTextures.indexOf(val) === -1) {
          isPresent = false;
        }
      });
      if (!isPresent) {
        updatedMaterialDetails.splice(index, 1);
      }
    });
    dispatch(configuratorActions.setInitialMaterialApplied(true));
    if (!updatedMaterialDetails?.length) {
      configuratorModule.projectModule.SendRoomLoadedCompletelyInfo();
    } else {
      const updatedData = updateDefaultTextureData({ data: updatedMaterialDetails });
      await loadUnityMaterialDetails({
        materialDetails: updatedData,
        unityContext: configuratorModule,
        unityLoadedSelector: selectUnityLoaded,
      });
    }
    resetProperties();
  };

  return {
    getAllDefaultTextures,
  };
};